import { useContext, useState } from 'preact/hooks';
import { api } from 'api';
import { ApplicationContext } from 'contexts/use-application-setup';
import { sessionStore } from 'global/session.store';
import {
  ForgotPasswordErrors,
  ResetPasswordFormData,
  ResetPasswordErrors,
  ErrorMessages,
} from 'types/password-reset';

export const usePasswordReset = () => {
  const { accountIdCode, href } = useContext(ApplicationContext);
  const [isLoadingForgot, setIsLoadingForgot] = useState(false);
  const [isLoadingReset, setIsLoadingReset] = useState(false);
  const [forgotErrors, setForgotErrors] = useState<ForgotPasswordErrors>({});
  const [resetErrors, setResetErrors] = useState<ResetPasswordErrors>({});

  const sendResetEmail = async (email: string) => {
    setIsLoadingForgot(true);
    setForgotErrors({});

    try {
      const response = await api.post('auth/forgot', {
        email,
        account: accountIdCode,
        site: href,
      });

      if (response?.code === 200) {
        return { success: true };
      }

      if (response?.errors?.email) {
        setForgotErrors({ email: response.errors.email });
      }

      return { success: false };
    } catch (error) {
      if (!error?.response) {
        return { success: false };
      }

      const parsedError =
        typeof error.response === 'string' ? JSON.parse(error.response) : error.response;

      return {
        success: false,
        accountError: parsedError?.errors?.account,
      };
    } finally {
      setIsLoadingForgot(false);
    }
  };

  const resetPassword = async (
    formData: ResetPasswordFormData,
    translate: (key: string) => string,
  ) => {
    setIsLoadingReset(true);
    setResetErrors({});

    try {
      const response = await api.post('auth/recovery', {
        ...formData,
        account: accountIdCode,
        isAccountPage: false,
      });

      if (response?.code === 200 && response.data) {
        sessionStore.login({
          token: response.data.token,
          user: response.data.user,
        });
        return { success: true };
      }

      return { success: false };
    } catch (error) {
      const fallbackMessage = translate('errors.login.INPUT_VALIDATION_ERR');

      if (!error?.response) {
        return { success: false, message: fallbackMessage };
      }

      let errorData;
      try {
        errorData = JSON.parse(error.response);
      } catch {
        return { success: false, message: fallbackMessage };
      }

      const { errors: apiErrors, message } = errorData ?? {};

      if (Array.isArray(apiErrors)) {
        const newErrors: ResetPasswordErrors = {};

        apiErrors.forEach(({ errorType }) => {
          switch (errorType) {
            case 'PWD_RESET_FAILED_INCOMPLIANT_PASSWORD': {
              const errorMsg = translate(ErrorMessages.PWD_RESET_FAILED_INCOMPLIANT_PASSWORD);
              newErrors.password = errorMsg;
              newErrors.password_confirm = errorMsg;
              break;
            }
            case 'PWD_RESET_FAILED_CHECKSUM_MISMATCH': {
              const errorMsg = translate(ErrorMessages.PWD_RESET_FAILED_CHECKSUM_MISMATCH);
              newErrors.code = errorMsg;
              break;
            }
            default:
              console.warn(errorType);
              break;
          }
        });

        setResetErrors(newErrors);
      } else if (apiErrors) {
        setResetErrors({
          code: apiErrors.code,
          password: apiErrors.password,
          password_confirm: apiErrors.password_confirm,
        });
      }

      return { success: false, message, errors: apiErrors };
    } finally {
      setIsLoadingReset(false);
    }
  };

  const clearForgotError = (fieldName: keyof ForgotPasswordErrors) => {
    setForgotErrors((prev) => {
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };

  const clearResetError = (fieldName: keyof ResetPasswordErrors) => {
    setResetErrors((prev) => {
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };

  const resetState = () => {
    setIsLoadingForgot(false);
    setIsLoadingReset(false);
    setForgotErrors({});
    setResetErrors({});
  };

  return {
    sendResetEmail,
    forgotErrors,
    clearForgotError,
    isLoadingForgot,

    resetPassword,
    resetErrors,
    clearResetError,
    isLoadingReset,

    resetState,
  };
};
